import {
  AppBar,
  Box,
  Button,
  Container,
  Stack,
  Toolbar,
  Typography,
  useScrollTrigger,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { FiPhone, FiCalendar } from 'react-icons/fi';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import logo from '@assets/logo512.png';
import { MobileDrawer } from './MobileDrawer';
import { NavbarButtonLink } from './NavbarButtonLink';
import {
  navbarButtons,
  navbarRightLinks,
  CONTACT_INFO,
} from './NavbarNavigateOptions';
import LanguageMenu from '../../menu/LanguageMenu';
import { ServicesPopover } from './ServicesPopover';
import {
  NAVBAR_CTA_BOOK_CONSULTATION,
  NAVBAR_CTA_CALL_NOW,
} from '../../../locales/types';

export const MainNabar = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Subtle elevation/blur when the user scrolls past the top contact bar.
  const scrolled = useScrollTrigger({
    disableHysteresis: true,
    threshold: 24,
  });

  return (
    <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: theme.zIndex.appBar }}>
      {/* Main navigation */}
      <AppBar
        position="static"
        elevation={scrolled ? 4 : 0}
        sx={{
          bgcolor: scrolled ? alpha(theme.palette.primary.main, 0.96) : 'primary.main',
          backdropFilter: scrolled ? 'saturate(180%) blur(8px)' : 'none',
          transition: 'all .25s ease',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 64, md: scrolled ? 64 : 76 },
              transition: 'min-height .25s ease',
              gap: 2,
            }}
          >
            {/* Left: mobile menu + logo */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
              <Box sx={{ display: { xs: 'inline-flex', lg: 'none' } }}>
                <MobileDrawer />
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => navigate('/')}
                aria-label="Linares Multiservices home"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.25,
                  background: 'transparent',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  p: 0,
                }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="Linares Multiservices"
                  sx={{
                    width: { xs: 38, md: 44 },
                    height: { xs: 38, md: 44 },
                    objectFit: 'contain',
                  }}
                />
                <Stack spacing={-0.25} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 800,
                      fontSize: { md: '1.05rem', lg: '1.15rem' },
                      letterSpacing: 0.3,
                      lineHeight: 1.1,
                      color: 'common.white',
                    }}
                  >
                    Linares
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 11,
                      letterSpacing: 1.5,
                      textTransform: 'uppercase',
                      opacity: 0.85,
                      color: 'common.white',
                    }}
                  >
                    Multiservices
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            {/* Center: primary nav (desktop) */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                display: { xs: 'none', lg: 'flex' },
                ml: 4,
                flexGrow: 1,
              }}
            >
              {navbarButtons.map((item) => (
                <NavbarButtonLink key={item?.title} item={item} />
              ))}
              <ServicesPopover />
              {navbarRightLinks.map((item) => (
                <NavbarButtonLink key={item?.title} item={item} />
              ))}
            </Stack>

            {/* Spacer for mobile to push CTA right */}
            <Box sx={{ flexGrow: 1, display: { lg: 'none' } }} />

            {/* Right: CTA + language */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                component="a"
                href={CONTACT_INFO.phoneHref}
                startIcon={<FiPhone />}
                variant="text"
                sx={{
                  color: 'common.white',
                  display: { xs: 'none', md: 'inline-flex' },
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { bgcolor: alpha('#fff', 0.08) },
                }}
              >
                <Trans i18nKey={`component.navbar.${NAVBAR_CTA_CALL_NOW}`} />
              </Button>
              <Button
                onClick={() => navigate('/support')}
                startIcon={<FiCalendar />}
                variant="contained"
                color="secondary"
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 999,
                  px: 2.25,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 2 },
                }}
              >
                <Trans i18nKey={`component.navbar.${NAVBAR_CTA_BOOK_CONSULTATION}`} />
              </Button>
              <LanguageMenu />
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>
    </Box>
  );
};
